'use client';
// components/SensorCardDemo.tsx
import React, { useState, useEffect } from 'react';
import { Cloud, Sprout, Cpu, Droplets, Sun, Activity, Zap } from 'lucide-react';
import { LucideIcon } from 'lucide-react';

interface SensorCard {
  icon: LucideIcon;
  iconBg: string;
  gradientFrom: string;
  gradientTo: string;
  label: string;
  value: string;
  status: string;
  statusColor: string;
}

const sensorDataSets: SensorCard[][] = [
  // 세트 1: 기본 환경
  [
    {
      icon: Cloud,
      iconBg: 'bg-blue-500',
      gradientFrom: 'from-blue-50',
      gradientTo: 'to-blue-100',
      label: '온도',
      value: '24.5°C',
      status: '정상',
      statusColor: 'text-green-500',
    },
    {
      icon: Sprout,
      iconBg: 'bg-green-500',
      gradientFrom: 'from-green-50',
      gradientTo: 'to-green-100',
      label: '토양 습도',
      value: '65%',
      status: '양호',
      statusColor: 'text-green-500',
    },
    {
      icon: Cpu,
      iconBg: 'bg-purple-500',
      gradientFrom: 'from-purple-50',
      gradientTo: 'to-purple-100',
      label: 'AI 분석',
      value: '최적화 완료',
      status: '활성',
      statusColor: 'text-green-500',
    },
  ],
  // 세트 2: 수분 관리
  [
    {
      icon: Droplets,
      iconBg: 'bg-cyan-500',
      gradientFrom: 'from-cyan-50',
      gradientTo: 'to-cyan-100',
      label: '습도',
      value: '72%',
      status: '적정',
      statusColor: 'text-green-500',
    },
    {
      icon: Activity,
      iconBg: 'bg-indigo-500',
      gradientFrom: 'from-indigo-50',
      gradientTo: 'to-indigo-100',
      label: 'EC 농도',
      value: '1.8 mS/cm',
      status: '정상',
      statusColor: 'text-green-500',
    },
    {
      icon: Zap,
      iconBg: 'bg-yellow-500',
      gradientFrom: 'from-yellow-50',
      gradientTo: 'to-yellow-100',
      label: 'pH 수치',
      value: '6.5',
      status: '최적',
      statusColor: 'text-green-500',
    },
  ],
  // 세트 3: 광 환경
  [
    {
      icon: Sun,
      iconBg: 'bg-orange-500',
      gradientFrom: 'from-orange-50',
      gradientTo: 'to-orange-100',
      label: '조도',
      value: '35,000 lux',
      status: '충분',
      statusColor: 'text-green-500',
    },
    {
      icon: Cloud,
      iconBg: 'bg-slate-500',
      gradientFrom: 'from-slate-50',
      gradientTo: 'to-slate-100',
      label: 'CO₂ 농도',
      value: '850 ppm',
      status: '적정',
      statusColor: 'text-green-500',
    },
    {
      icon: Activity,
      iconBg: 'bg-rose-500',
      gradientFrom: 'from-rose-50',
      gradientTo: 'to-rose-100',
      label: '생육 상태',
      value: '건강',
      status: '양호',
      statusColor: 'text-green-500',
    },
  ],
  // 세트 4: 종합 모니터링
  [
    {
      icon: Droplets,
      iconBg: 'bg-teal-500',
      gradientFrom: 'from-teal-50',
      gradientTo: 'to-teal-100',
      label: '관수량',
      value: '2.5 L/h',
      status: '정상',
      statusColor: 'text-green-500',
    },
    {
      icon: Sprout,
      iconBg: 'bg-lime-500',
      gradientFrom: 'from-lime-50',
      gradientTo: 'to-lime-100',
      label: '영양분',
      value: '최적',
      status: '충분',
      statusColor: 'text-green-500',
    },
    {
      icon: Cpu,
      iconBg: 'bg-violet-500',
      gradientFrom: 'from-violet-50',
      gradientTo: 'to-violet-100',
      label: '자동 제어',
      value: '작동 중',
      status: '활성',
      statusColor: 'text-green-500',
    },
  ],
];

export default function SensorCardDemo() {
  const [currentSetIndex, setCurrentSetIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);

      setTimeout(() => {
        setCurrentSetIndex((prev) => (prev + 1) % sensorDataSets.length);
        setIsTransitioning(false);
      }, 300);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const currentSet = sensorDataSets[currentSetIndex];

  return (
    <div className="space-y-4">
      {currentSet.map((sensor, index) => (
        <div
          key={`${currentSetIndex}-${index}`}
          className={`flex items-center justify-between p-4 bg-gradient-to-r ${sensor.gradientFrom} ${sensor.gradientTo} dark:from-${
            sensor.iconBg.split('-')[1]
          }-900/20 dark:to-${sensor.iconBg.split('-')[1]}-800/20 rounded-lg transition-all duration-300 ${
            isTransitioning ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className={`p-2 ${sensor.iconBg} rounded-lg`}>
              <sensor.icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{sensor.label}</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{sensor.value}</div>
            </div>
          </div>
          <div className={`${sensor.statusColor} text-sm font-medium`}>{sensor.status}</div>
        </div>
      ))}
    </div>
  );
}
